const STORAGE_KEY = 'memberNestLoginAttemptStateV1';
const FAILURE_WINDOW_MS = 10 * 60 * 1000;
const LOCKOUT_MS = 15 * 60 * 1000;
const MAX_FAILURES = 5;

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function safeNow() {
  return Date.now();
}

function getStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch (err) {
    return null;
  }
}

function readState() {
  const storage = getStorage();
  if (!storage) return {};
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    return {};
  }
}

function writeState(state) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    // Login should not fail just because browser storage is unavailable.
  }
}

function fnv1a(value) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function getAttemptKey(email) {
  const normalizedEmail = normalizeEmail(email);
  return normalizedEmail ? `email:${fnv1a(normalizedEmail)}` : 'email:empty';
}

function cleanupState(state, now = safeNow()) {
  return Object.entries(state).reduce((acc, [key, value]) => {
    const lockedUntil = Number(value?.lockedUntil || 0);
    const lastFailureAt = Number(value?.lastFailureAt || 0);
    if (lockedUntil > now || now - lastFailureAt < LOCKOUT_MS * 2) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function getLoginThrottle(email, now = safeNow()) {
  const state = cleanupState(readState(), now);
  const record = state[getAttemptKey(email)] || {};
  const lockedUntil = Number(record.lockedUntil || 0);
  if (lockedUntil > now) {
    return {
      blocked: true,
      retryAfterSeconds: Math.max(1, Math.ceil((lockedUntil - now) / 1000)),
    };
  }
  return { blocked: false, retryAfterSeconds: 0 };
}

export function recordLoginFailure(email, now = safeNow()) {
  const state = cleanupState(readState(), now);
  const key = getAttemptKey(email);
  const existing = state[key] || {};
  const firstFailureAt = Number(existing.firstFailureAt || 0);
  const withinWindow = firstFailureAt && now - firstFailureAt < FAILURE_WINDOW_MS;
  const failureCount = withinWindow ? Number(existing.failureCount || 0) + 1 : 1;
  const lockedUntil = failureCount >= MAX_FAILURES ? now + LOCKOUT_MS : 0;

  state[key] = {
    firstFailureAt: withinWindow ? firstFailureAt : now,
    lastFailureAt: now,
    failureCount,
    lockedUntil,
  };
  writeState(state);

  return {
    failureCount,
    locked: lockedUntil > now,
    retryAfterSeconds: lockedUntil > now ? Math.ceil((lockedUntil - now) / 1000) : 0,
  };
}

export function recordLoginSuccess(email) {
  const state = readState();
  const key = getAttemptKey(email);
  if (!state[key]) return;
  delete state[key];
  writeState(cleanupState(state));
}

export function formatLoginRetryDelay(seconds) {
  const totalSeconds = Math.max(1, Number(seconds) || 1);
  const minutes = Math.ceil(totalSeconds / 60);
  return minutes <= 1 ? 'about 1 minute' : `about ${minutes} minutes`;
}

export function buildAccountLabel(email) {
  const normalizedEmail = normalizeEmail(email);
  const [rawLocal = '', rawDomain = ''] = normalizedEmail.split('@');
  const domain = rawDomain.replace(/[^a-z0-9.-]/g, '').slice(0, 120);
  const local = rawLocal.replace(/\s+/g, '').slice(0, 120);
  if (!domain) return '';
  if (!local) return `(no local part) (${domain})`;
  const head = local.slice(0, Math.min(2, local.length));
  const tail = local.length > 3 ? local.slice(-2) : '';
  return `${head}...${tail} (${domain})`;
}

async function sha256Hex(value) {
  if (
    typeof window === 'undefined'
    || !window.crypto?.subtle
    || typeof TextEncoder === 'undefined'
  ) {
    return '';
  }
  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch (err) {
    return '';
  }
}

export async function buildLoginAttemptTelemetry(email) {
  const normalizedEmail = normalizeEmail(email);
  const [, rawDomain = ''] = normalizedEmail.split('@');
  const domain = rawDomain.replace(/[^a-z0-9.-]/g, '').slice(0, 120);
  const accountHash = normalizedEmail ? await sha256Hex(normalizedEmail) : '';
  return {
    accountHash,
    accountHashVersion: accountHash ? 'sha256:normalized-email:v1' : '',
    accountLabel: buildAccountLabel(normalizedEmail),
    accountDomain: domain,
    accountLocalLength: normalizedEmail.split('@')[0]?.length || 0,
  };
}
